import "isomorphic-fetch";
import {
    Client,
    BatchRequestStep,
    BatchRequestContent,
    BatchResponseContent,
    GraphRequest
} from "@microsoft/microsoft-graph-client/lib/src/browser";
import authModule from "../../../utilities/AuthModule";
import { IWellKnownFolder } from "../interfaces/IWellKnownFolder";
import { WellKnownFolders } from "../config/graphConstants";
import { logger } from "../../../utilities/logger";
import { IMailFolder } from "../interfaces/IMailFolder";
import { CacheHelper } from "../../../utilities/CacheHelper";
import { DocubirdCache } from "../config/constants";
import { getAuthenticatedClient } from "./GraphClientService";

const wellKnownFoldersStorageKey: string = "wellKnownFolders";
const folderStorageKey: string = "exchangeFolders";

export interface IExchangeMessages {
    messages: any[];
    nextLink?: string;
}

export const getWellKnownFolders = async (mailboxId: string): Promise<IWellKnownFolder[]> => {
    try {
        const cacheKey = `${wellKnownFoldersStorageKey}-${mailboxId}`
        const cachedWellKnownFolders = CacheHelper.get(cacheKey);
        if (cachedWellKnownFolders) {
            return cachedWellKnownFolders as IWellKnownFolder[];
        }

        // const client = getAuthenticatedClient(await authModule.getMailboxAccessToken());
        const client = getAuthenticatedClient(await authModule.getGraphAccessToken());

        const wellKnownFolderResults: IWellKnownFolder[] = [];

        for (let i = 0; i < WellKnownFolders.length; i += 20) {
            const wellKnownFolderSlices: IWellKnownFolder[] = WellKnownFolders.slice(i, i + 20);

            const batchRequests: BatchRequestStep[] = wellKnownFolderSlices.map(wellKnownFolder => {
                return {
                    id: wellKnownFolder.name,
                    request: new Request(`/users/${mailboxId}/mailFolders/${wellKnownFolder.name}?$select=id`, {
                        method: "GET"
                    })
                };
            });

            const content = await new BatchRequestContent(batchRequests).getContent();
            const batchResponse = await client.api("/$batch").post(content);
            const batchResponseContent = new BatchResponseContent(batchResponse);

            await Promise.all(wellKnownFolderSlices.map(async wellKnownFolder => {
                const wellKnownFolderResponse = batchResponseContent.getResponseById(wellKnownFolder.name);
                if (wellKnownFolderResponse.ok) {
                    const wellKnownFolderId: string = (await wellKnownFolderResponse.json())?.id;
                    if (wellKnownFolderId) {
                        wellKnownFolderResults.push({ id: wellKnownFolderId, name: wellKnownFolder.name, icon: wellKnownFolder.icon });
                    }
                }
            }));
        }

        CacheHelper.set(cacheKey, wellKnownFolderResults);

        return wellKnownFolderResults;
    } catch (error) {
        logger(error, "ExchangeSerivce.ts (getWellKnownFolders)");
        return [];
    }
};

export const getExchangeFolders = async (mailboxId: string, filter: string, top?: number, saveToCache: boolean = true): Promise<IMailFolder[]> => {
    try {
        const cacheKey = `${folderStorageKey}-${mailboxId}`
        if (!filter) {
            const cachedFolders = CacheHelper.get(cacheKey);
            if (cachedFolders) {
                return cachedFolders as IMailFolder[];
            }
        }

        // const client = getAuthenticatedClient(await authModule.getMailboxAccessToken());
        const client = getAuthenticatedClient(await authModule.getGraphAccessToken());

        const baseRequestURL: string = `/users/${mailboxId}/mailFolders`;
        let folderRequest: GraphRequest = client.api(baseRequestURL)
            .top(top ? top : 999);

        if (filter) {
            folderRequest.filter(`contains(displayName, '${filter}')`);
        }

        let folders: IMailFolder[] = (await folderRequest.get())?.value;

        let parentFolders: IMailFolder[] = folders
            ?.filter(folder => folder.childFolderCount > 0);

        if (parentFolders?.length > 0) {
            await getExchangeChildFolders(client, parentFolders, baseRequestURL);
        }

        if (!filter && saveToCache) {
            CacheHelper.set(cacheKey, folders);
        }

        return folders;
    } catch (error) {
        logger(error, "ExchangeSerivce.ts (getMailboxFolders)");
        return [];
    }
}

export const getExchangeMessages = async (folderId: string, filter: string, orderBy: string, ascDesc: string, mailboxId: string, nextLink?: string): Promise<IExchangeMessages> => {
    let result: any;

    try {
        // const client = getAuthenticatedClient(await authModule.getMailboxAccessToken());
        const client = getAuthenticatedClient(await authModule.getGraphAccessToken());

        if (nextLink?.length > 0) {
            result = await client.api(nextLink).get();
        }
        else {
            const request: GraphRequest = client.api(`/users/${mailboxId}/mailFolders/${folderId}/messages`)
                .select("id,bccRecipients,bodyPreview,ccRecipients,conversationId,categories,hasAttachments,importance,internetMessageId,parentFolderId,receivedDateTime,replyTo,sender,sentDateTime,subject,toRecipients")
                .top(200);

            if (filter && orderBy) { // https://learn.microsoft.com/en-us/graph/api/user-list-messages?view=graph-rest-1.0&tabs=http#using-filter-and-orderby-in-the-same-query
                let filterQuery: string = "";
                switch (orderBy) {
                    case "receivedDateTime":
                        filterQuery = "receivedDateTime ge 1900-01-01T00:00:00Z and ";
                        break;
                    case "sender/emailAddress/name":
                        filterQuery = "sender/emailAddress/name ne '' and ";
                        break;
                    default:
                        break;
                }

                if (filter.startsWith("@")) {
                    filter = filter.replace("@", "");
                    filterQuery += `(contains(sender/emailAddress/name, '${filter}') or contains(subject, '${filter}'))`;
                }
                else {
                    filterQuery += `contains(subject, '${filter}')`;
                }

                request.filter(filterQuery);
                request.orderby(`${orderBy} ${ascDesc}`);
            }
            else {
                if (filter) {
                    if (filter.startsWith("@")) {
                        filter = filter.replace("@", "");
                        request.filter(`contains(sender/emailAddress/name, '${filter}') or contains(subject, '${filter}')`);
                    }
                    else {
                        request.filter(`contains(subject, '${filter}')`);
                    }
                }
                if (orderBy) request.orderby(`${orderBy} ${ascDesc}`);
            }

            result = await request.get();
        }
    } catch (error) {
        logger(error, "ExchangeSerivce.ts (getExchangeMessages)");
    }

    return {
        messages: result?.value ?? [],
        nextLink: result ? result["@odata.nextLink"] : undefined
    }
}

export const getExchangeMessageHTMLBody = async (mailboxId: string, messageId: string): Promise<string> => {
    try {
        // const client = getAuthenticatedClient(await authModule.getMailboxAccessToken());
        const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
        const message = await client.api(`/users/${mailboxId}/messages/${messageId}`)
            .select("body")
            .get();

        return message?.body?.content;
    } catch (error) {
        logger(error, "ExchangeSerivce.ts (getExchangeMessageHTMLBody)");
    }
    return undefined;
}

export const getExchangeMessageAttachments = async (mailboxId: string, messageId: string): Promise<any[]> => {
    try {
        // const client = getAuthenticatedClient(await authModule.getMailboxAccessToken());
        const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
        const messageAttachments = await client.api(`/users/${mailboxId}/messages/${messageId}/attachments`)
            .select("id,name,lastModifiedDateTime,isInline")
            .get();

        return Array.isArray(messageAttachments?.value) ? messageAttachments.value.filter(a => !a.isInline) : [];
    } catch (error) {
        logger(error, "ExchangeSerivce.ts (getExchangeMessageAttachments)");
    }
    return undefined;
}

const getExchangeMessage = async (mailboxId: string, messageId: string): Promise<any> => {
    try {
        // const client = getAuthenticatedClient(await authModule.getMailboxAccessToken());
        const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
        const message = await client.api(`/users/${mailboxId}/messages/${messageId}`)
            .select("id,bccRecipients,ccRecipients,conversationId,categories,hasAttachments,importance,internetMessageId,receivedDateTime,replyTo,sender,sentDateTime,subject,toRecipients")
            .get();
        return message;
    } catch (error) {
        logger(error, "ExchangeSerivce.ts (getExchangeMessageAttachments)");
    }
    return undefined;
}

const getExchangeChildFolders = async (client: Client, parentFolders: IMailFolder[], baseRequestURL: string) => {
    for (let i = 0; i < parentFolders.length; i += 20) {
        const parentFolderSlices: IMailFolder[] = parentFolders.slice(i, i + 20);

        // Use synthetic, sequential batch ids. Graph treats the batch request id as
        // case-insensitive and rejects the whole batch (400) on duplicates; Exchange
        // folder ids are case-sensitive base64 tokens that can collide case-insensitively.
        const batchRequests: BatchRequestStep[] = parentFolderSlices.map((parentFolder, index) => {
            return {
                id: `${index}`,
                request: new Request(`${baseRequestURL}/${parentFolder.id}/childFolders?$top=1000`, {
                    method: "GET"
                })
            };
        });

        const content = await new BatchRequestContent(batchRequests).getContent();
        const batchResponse = await client.api("/$batch").post(content);
        const batchResponseContent = new BatchResponseContent(batchResponse);

        await Promise.all(parentFolderSlices.map(async (parentFolder, index) => {
            const childFolderResponse = batchResponseContent.getResponseById(`${index}`);
            if (childFolderResponse.ok) {
                const childFolders: IMailFolder[] = (await childFolderResponse.json())?.value;
                if (childFolders) {
                    let nestedParentFolders: IMailFolder[] = childFolders
                        ?.filter(folder => folder.childFolderCount > 0);

                    if (nestedParentFolders?.length > 0) {
                        await getExchangeChildFolders(client, nestedParentFolders, baseRequestURL);
                    }

                    let folder: IMailFolder = parentFolders.find(f => f.id === parentFolder.id);
                    if (folder) {
                        folder.childFolders = childFolders;
                    }
                }
            }
        }));
    }
}

export const getSelectedExchangeEmails = async (messageIds: string[], mailboxId: string, folderId: string) => {
    try {
        // const client = getAuthenticatedClient(await authModule.getMailboxAccessToken());
        const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
        const baseRequestURL: string = `/users/${mailboxId}/mailFolders/${folderId}/messages`;
        const propertySelection: string = "id,bccRecipients,ccRecipients,conversationId,categories,hasAttachments,importance,internetMessageId,receivedDateTime,replyTo,sender,sentDateTime,subject,toRecipients";
        const messages: any[] = [];

        for (let i = 0; i < messageIds.length; i += 20) {
            const messagesSlices: any[] = messageIds.slice(i, i + 20);

            // Use synthetic, sequential batch ids. Graph treats the batch request id as
            // case-insensitive and rejects the whole batch (400) on duplicates; Exchange
            // message ids are case-sensitive base64 tokens that can collide case-insensitively.
            const batchRequests: BatchRequestStep[] = messagesSlices.map((messageId, index) => {
                return {
                    id: `${index}`,
                    request: new Request(`${baseRequestURL}/${messageId}/?$select=${propertySelection}`, {
                        method: "GET"
                    })
                };
            });

            const content = await new BatchRequestContent(batchRequests).getContent();
            const batchResponse = await client.api("/$batch").post(content);
            const batchResponseContent = new BatchResponseContent(batchResponse);

            await Promise.all(messagesSlices.map(async (messageId, index) => {
                const messageResponse = batchResponseContent.getResponseById(`${index}`);
                let message: any;
                if (messageResponse.ok) {
                    message = (await messageResponse.json());
                }
                else {
                    console.warn("Exchange mail retrieval retry triggered: ", messageResponse.status, messageResponse.statusText);
                    message = await getExchangeMessage(mailboxId, messageId);
                }

                if (message) {
                    messages.push(message);
                }
            }));
        }

        CacheHelper.set(DocubirdCache.selectedExchangeItems, messages);
    } catch (error) {
        logger(error, "ExchangeSerivce.ts (getSelectedExchangeEmails)");
    }
}